import dayjs from 'dayjs';
import React from 'react';
import { Formik, Form } from 'formik';

import { Grid, CircularProgress } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

import { paymentFiltersStyles } from '../../styles/styles';
import { schema, initialValues } from '../../utils/paymentSchema';
import { Autocomplete, Button, TextField, Typography } from 'shared';
import { TYPE_OPTIONS, STATUS_OPTIONS, MARKETPLACE_OPTIONS } from '../../utils/paymentOptions';

const PaymentFilters = ({ handleFiltersSubmit, isFetchingInvoices, isFetchingPayments }) => {
    const classes = paymentFiltersStyles();

    const handleClearClick = (handleReset) => {
        handleReset();
        handleFiltersSubmit({});
    };

    const handleFormSubmit = (values) => {
        const filters = {
            ...values,
            startDate: values.startDate ? dayjs(values.startDate).format('YYYY-MM-DD') : null,
            endDate: values.endDate ? dayjs(values.endDate).format('YYYY-MM-DD') : null,
            marketplace: values.marketplace?.value,
            type: values.type?.value,
            status: values.status?.value,
        };
        handleFiltersSubmit(filters);
    };

    return (
        <Formik onSubmit={handleFormSubmit} validationSchema={schema} initialValues={initialValues}>
            {({ values, handleReset, handleChange, setFieldValue }) => {
                return (
                    <Form>
                        <Grid container spacing={1}>
                            <Grid item xs={4} md={2} lg={2}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        value={values.startDate && dayjs(values.startDate)}
                                        onChange={(date) => setFieldValue('startDate', date)}
                                        label="Start Date"
                                        placeholder="Start Date"
                                        format="MM-DD-YYYY"
                                        sx={classes.datePicker}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            <Grid item xs={4} md={2} lg={2}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        value={values.endDate && dayjs(values.endDate)}
                                        onChange={(date) => setFieldValue('endDate', date)}
                                        label="End Date"
                                        placeholder="End Date"
                                        format="MM-DD-YYYY"
                                        sx={classes.datePicker}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            <Grid item xs={4} md={2} lg={2}>
                                <TextField
                                    fullWidth
                                    name="amount"
                                    inlineLabel="Amount"
                                    value={values.amount}
                                    placeholder="Search by amount"
                                    customStyle={classes.inputField}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^-?\d*\.?\d*$/.test(val)) {
                                            handleChange(e);
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={4} md={2} lg={2}>
                                <Autocomplete
                                    options={TYPE_OPTIONS}
                                    labelKey="label"
                                    valueKey="value"
                                    placeholder="Type"
                                    name="type"
                                    inlineLabel="Type"
                                    value={values.type}
                                    onChange={(e, value) => setFieldValue('type', value)}
                                    isSearchField={false}
                                    disableClearable
                                    customStyle={classes.dropdown}
                                />
                            </Grid>

                            <Grid item xs={4} md={2} lg={2}>
                                <Autocomplete
                                    options={STATUS_OPTIONS}
                                    labelKey="label"
                                    valueKey="value"
                                    placeholder="Status"
                                    name="status"
                                    inlineLabel="Status"
                                    value={values.status}
                                    onChange={(e, value) => setFieldValue('status', value)}
                                    isSearchField={false}
                                    disableClearable
                                    customStyle={classes.dropdown}
                                />
                            </Grid>

                            <Grid item xs={4} md={2} lg={2}>
                                <Autocomplete
                                    options={MARKETPLACE_OPTIONS}
                                    labelKey="label"
                                    valueKey="value"
                                    placeholder="Marketplace"
                                    name="marketplace"
                                    inlineLabel="Marketplace"
                                    value={values.marketplace}
                                    onChange={(e, value) => setFieldValue('marketplace', value)}
                                    isSearchField={false}
                                    disableClearable
                                    customStyle={classes.dropdown}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} alignItems="center" justifyContent="flex-end">
                            {(isFetchingInvoices || isFetchingPayments) && (
                                <Grid item sx={{ display: 'flex', alignItems: 'center', marginRight: '16px' }}>
                                    <CircularProgress size={16} sx={{ marginRight: '8px' }} />
                                    <Typography
                                        text={
                                            isFetchingInvoices && isFetchingPayments
                                                ? 'Fetching Invoices and Payments'
                                                : isFetchingInvoices
                                                    ? 'Fetching Invoices'
                                                    : 'Fetching Payments'
                                        }
                                        variant="body2"
                                    />
                                </Grid>
                            )}

                            <Grid item marginTop="10px" marginBottom="10px">
                                <Button
                                    text="Clear"
                                    onClick={() => handleClearClick(handleReset)}
                                    customStyles={classes.clearButton}
                                />
                                <Button text="Apply" type="submit" />
                            </Grid>
                        </Grid>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default PaymentFilters;
